import React from "react";
import { DataGrid } from "@mui/x-data-grid";
import {
  Analysis,
  StudentAverageAnalysis,
  ItemType,
  QuestionType,
  Team,
  TeamAverageAnalysis,
  Template,
  User,
  GenericAverageAnalysis,
  ResponseSubjectType,
  AggregateComments,
  PointRow,
  Campaign,
  ItemSubjectType,
} from "../../types";
import { Box, Stack, Typography } from "@mui/material";
import { Warning } from "@mui/icons-material";
import {
  VictoryAxis,
  VictoryBar,
  VictoryChart,
  VictoryLabel,
  VictoryLegend,
  VictoryScatter,
  VictoryTheme,
  VictoryTooltip,
  CallbackArgs,
} from "victory";

import { useResizeDetector } from "react-resize-detector";
import { getUserName } from "../../DataTransform";
import FeedbackLoopColors from "./Colors";
import {
  IndividualSelfPeerOutlierColumns,
  TeamSelfPeerOutlierColumns,
  GroupColumns,
  IndividualColumns,
  IndividualOutlierColumns,
  TeamOutlierColumns,
  IndividualTextColumns,
  GroupTextColumns,
  IndividualPointColumns,
  GroupPointColumns,
  AverageColumns,
  IndividualGivenColumns,
  GroupGivenColumns,
  AverageGivenColumns,
  IndividualYesNoColumns,
  GroupYesNoColumns,
  IndividualGivenTextColumns,
  getIndividualSelfPeerColumns,
  getGroupSelfPeerColumns,
  getAverageSelfPeerColumns,
  getIndividualSelfPeerYesNoColumns,
  getGroupSelfPeerYesNoColumns,
  hasMultipleRaterTypes,
} from "./Columns";
import { sortAnalysesByTeamThenName, truncateAtWordBoundary } from "./labelHelpers";
import isCompatible, {
  isSubjectCompatible,
} from "../SubjectType/compatibility";
import { InstructorLoopResponse } from "../Results/InstructorLoopResponse";
import ExportMenu from "./ExportMenu";

export function formatYesNoValue(value: number | undefined): string {
  if (value === undefined) return "";
  if (value === 1) return "Yes";
  if (value === 0) return "No";
  return `${(value * 100).toFixed(0)}% Yes`;
}

export function analysesToObjectArray(
  analyses: Analysis[],
  aggregates: AggregateComments[],
  pointRows: PointRow[],
  sections: string[],
  user?: User,
) {
  return sections.map((section, id) => {
    // Filter analyses by section
    const sectionAnalyses = analyses.filter(
      (analysis) => analysis.question.category === section,
    );

    // Filter yes/no analyses by section
    const yesNoAnalyses = sectionAnalyses.filter(
      (analysis) => analysis.question.questionType === QuestionType.YesNo,
    );

    // Filter other analyses by section
    const otherAnalyses = sectionAnalyses.filter(
      (analysis) => analysis.question.questionType !== QuestionType.YesNo,
    );

    return {
      id,
      section,
      analyses: otherAnalyses.map((analysis, id) => ({
        id,
        section: analysis.question.category,
        question: analysis.question.label,
        user: getUserName(analysis.user),
        given: analysis.given,
        self: analysis.self,
        peers: analysis.others,
        gap: analysis.gap,
        othersInstructor: analysis.othersInstructor,
        othersStudent: analysis.othersStudent,
        othersLead: analysis.othersLead,
      })),
      yesNoAnalyses: yesNoAnalyses.map((analysis, id) => ({
        id,
        section: analysis.question.category,
        question: analysis.question.label,
        user: getUserName(analysis.user),
        given: analysis.given,
        self: analysis.self,
        peers: analysis.others,
        gap: analysis.gap,
        othersInstructor: analysis.othersInstructor,
        othersStudent: analysis.othersStudent,
        othersLead: analysis.othersLead,
      })),
      aggregates: aggregates
        // Filter aggregates for section
        .filter((aggregate) => aggregate.question.category === section)
        .map((aggregate, id) => ({
          id,
          section: aggregate.question.category,
          question: aggregate.question.label,
          receivedComments: aggregate.comments
            .filter((comment) => {
              // GL (General) questions have no meaningful recipient: the backend
              // sets recipient = respondent when subject_user is null.  Showing
              // "received" general feedback would be misleading, so we suppress
              // it entirely.  givenComments is intentionally NOT suppressed — in
              // reverse mode it's valid to show what general feedback the user
              // submitted ("what did I write?").
              if (aggregate.question.subjectType === ItemSubjectType.General)
                return false;
              if (!user) return true;
              return comment.recipient.id === user.id;
            })
            .map((comment) => ({
              ...comment,
              recipient: getUserName(comment.recipient),
              respondent: getUserName(comment.respondent),
            })),
          givenComments: aggregate.comments
            // If user is set, show only comments authored by that user.
            // GL questions are intentionally not suppressed here — see note above.
            .filter((comment) =>
              user ? comment.respondent.id === user.id : true,
            )
            .map((comment) => ({
              ...comment,
              recipient: getUserName(comment.recipient),
              respondent: getUserName(comment.respondent),
            })),
        })),
      pointRows: pointRows
        .filter((pointRow) => pointRow.question.category === section)
        .map((pointRow, id) => ({
          id,
          section: pointRow.question.category,
          question: pointRow.question.label,
          user: getUserName(pointRow.user),
          sender: getUserName(pointRow.sender),
          value: pointRow.value,
        })),
    };
  });
}

function questionsByStudent(users: User[], analyses: Analysis[]) {
  return users
    .map((user) => ({
      user,
      questions: analyses.filter((analysis) => analysis.user == user),
    }))
    .slice()
    .sort((itemA, itemB) =>
      itemA.user.firstName > itemB.user.firstName ? 1 : -1,
    );
}

function getDomainBounds(template: Template): [number, number] {
  const choiceQuestions = template.items.filter(
    (item) => item.itemType === ItemType.Choice,
  );

  if (!choiceQuestions.length) {
    return [0.5, 5.5]; // Default fallback
  }

  const allValues = choiceQuestions.flatMap((question) =>
    question.options.map((option) => option.value),
  );

  const min = Math.min(...allValues);
  const max = Math.max(...allValues);

  // If min is non-negative, start at 0, otherwise add padding below min
  const lowerBound = min >= 0 ? 0 : min - 0.5;

  return [lowerBound, max + 0.5];
}

export interface Props {
  reverse: boolean;
  subjectType: ResponseSubjectType;
  selectedTeamId?: string;
  selectedUserId?: string;
  showStudents?: boolean;
  showQuestions?: boolean;
  teams?: Team[];
  template: Template;
  user?: User;
  /** Whether the selected subject user is a team lead. Used to filter applicable questions. */
  selectedUserIsTeamLead?: boolean;
  /** Whether the selected subject user is an instructor. Used to filter applicable questions. */
  selectedUserIsInstructor?: boolean;
  onTeamClick?: (id: string) => void;
  onUserClick?: (id: string) => void;
  campaign?: Campaign;
  precomputedAnalyses: Analysis[];
  precomputedAggregates: AggregateComments[];
  precomputedPointRows: PointRow[];
  precomputedStudentAverages: Analysis[];
  precomputedTeamAverages: Analysis[];
  authorType?: string;
  hasMultipleRaterTypes?: boolean;
}

const GridHeaderFactory = (
  section: string,
  color?: string,
  icon?: JSX.Element,
) => {
  const GridHeader = () => (
    <Typography
      variant="subtitle1"
      sx={{
        pl: 1,
        pt: 0.5,
        pb: 0.5,
        backgroundColor: color ? color : "#222",
      }}
    >
      {section} {icon}
    </Typography>
  );
  return GridHeader;
};

export const AnalysisView = ({
  reverse,
  subjectType,
  selectedTeamId,
  selectedUserId,
  showQuestions = false,
  showStudents = false,
  teams,
  template,
  user,
  selectedUserIsTeamLead,
  selectedUserIsInstructor,
  onTeamClick,
  onUserClick,
  campaign,
  precomputedAnalyses,
  precomputedAggregates,
  precomputedPointRows,
  precomputedStudentAverages,
  precomputedTeamAverages,
  authorType,
  hasMultipleRaterTypes: hasMultipleRaterTypesProp,
}: Props) => {
  const multipleRaterTypes =
    hasMultipleRaterTypesProp ?? hasMultipleRaterTypes(template.items);
  const responseUsers: User[] = Array.from(
    new Map(precomputedAnalyses.map((a) => [a.user.id, a.user])).values(),
  );

  // When a specific subject (user) is selected and we know their role, use
  // isSubjectCompatible to filter questions to only those applicable for that
  // subject. This prevents non-applicable questions (e.g. LeadOnly for a
  // non-lead, or PeerSelf for an instructor) from cluttering the results view
  // and falsely suggesting feedback was gathered on those items.
  //
  // When no role info is provided (selectedUserIsTeamLead / selectedUserIsInstructor
  // are undefined), fall back to the broad isCompatible check so existing behavior
  // is preserved for callers that have not yet been updated to pass role info.
  const questions = template.items.filter((item) => {
    if (
      user !== undefined &&
      selectedUserIsTeamLead !== undefined &&
      selectedUserIsInstructor !== undefined
    ) {
      return isSubjectCompatible(
        subjectType,
        item.subjectType,
        selectedUserIsTeamLead,
        selectedUserIsInstructor,
      );
    }
    return isCompatible(subjectType, item.subjectType);
  });

  // When subject role info is provided, filter analyses/aggregates/pointRows to only
  // include data for applicable questions. This ensures that even if the backend
  // returns data for non-applicable questions, they won't appear in the results.
  // Without role info we skip this extra filter for backward compatibility.
  const hasSubjectRoleInfo =
    user !== undefined &&
    selectedUserIsTeamLead !== undefined &&
    selectedUserIsInstructor !== undefined;
  const applicableQuestionIds = hasSubjectRoleInfo
    ? new Set(questions.map((q) => q.id))
    : null;

  const safeTeams = teams || [];
  const analyses = (precomputedAnalyses || []).filter(
    (analysis) =>
      (user ? analysis.user.id === user.id : true) &&
      (applicableQuestionIds === null ||
        applicableQuestionIds.has(analysis.question.id)),
  );

  const aggregates = (precomputedAggregates || [])
    .filter(
      (agg) =>
        applicableQuestionIds === null ||
        applicableQuestionIds.has(agg.question.id),
    )
    .map((analysis) => ({
      ...analysis,
      comments: analysis.comments.filter((comment) => {
        if (!user || reverse) return true;
        return comment.recipient && comment.recipient.id === user.id;
      }),
    }));

  const pointRows = (precomputedPointRows || []).filter(
    (row) =>
      (!user || row.user.id === user.id) &&
      (applicableQuestionIds === null ||
        applicableQuestionIds.has(row.question.id)),
  );

  const avgsByStudent = precomputedStudentAverages
    ? [
        {
          user:
            user ??
            (precomputedStudentAverages.length
              ? precomputedStudentAverages[0].user
              : undefined),
          questions: sortAnalysesByTeamThenName(
            user
              ? precomputedStudentAverages.filter((a) => a.user.id === user.id)
              : precomputedStudentAverages,
            teams || [],
          ).map((a) => ({
            self: a.self,
            others: a.others,
            gap: a.gap,
            given: a.given,
            user: a.user,
            label: a.label,
            id: a.id!,
            outlier: a.outlier || false,
            othersInstructor: a.othersInstructor,
            othersStudent: a.othersStudent,
            othersLead: a.othersLead,
          })),
        },
      ]
    : [
        {
          user: analyses.length > 0 ? analyses[0].user : undefined,
          questions: [],
        },
      ];
  const analysesByStudent = showStudents
    ? !showQuestions
      ? avgsByStudent
      : questionsByStudent(responseUsers, analyses).filter(
          (analysis) => analysis.user.id === user?.id,
        )
    : precomputedTeamAverages
      ? [
          {
            user: precomputedTeamAverages.length
              ? precomputedTeamAverages[0].user
              : undefined,
            questions: precomputedTeamAverages.map((a) => ({
              self: a.self,
              others: a.others,
              gap: a.gap,
              given: a.given,
              team: { id: a.id!, name: a.label, userIds: [] } as Team,
              label: a.label,
              id: a.id!,
              outlier: a.outlier || false,
            })),
          },
        ]
      : [
          {
            user: analyses.length > 0 ? analyses[0].user : undefined,
            questions: [],
          },
        ];
  const filteredAverages = user
    ? avgsByStudent[0].questions.filter((a) => a.user.id === user.id)
    : avgsByStudent[0].questions;
  const averageAnalyses = filteredAverages.map((analysis) => ({
    ...analysis,
    user: getUserName(analysis.user),
  }));

  const outlierFilter = (
    question: StudentAverageAnalysis | TeamAverageAnalysis,
  ) => question.outlier;

  const outliers = showStudents
    ? !showQuestions
      ? (analysesByStudent[0].questions as StudentAverageAnalysis[]).filter(
          outlierFilter,
        )
      : []
    : (analysesByStudent[0].questions as TeamAverageAnalysis[]).filter(
        outlierFilter,
      );

  const sections = questions
    .map((question) => question.category)
    .filter((category, index, array) => array.indexOf(category) === index);

  // groupCount controls the chart domain x-range [0.5, groupCount+0.5].
  // It must equal the number of bars actually rendered, which in every mode is
  // analysesByStudent[0].questions.length.  Using a different source can cause
  // a domain mismatch in two directions:
  //   • Too small → bars at positions beyond groupCount are clipped and vanish.
  //   • Too large → bars cluster at the bottom of an over-sized domain, leaving
  //     most of the chart empty.
  //
  // Concrete failures:
  //   1. Student averages mode: responseUsers was derived from precomputedAnalyses
  //      only, so students who received no individual peer feedback were absent,
  //      making groupCount smaller than the data array (bars clipped, invisible).
  //   2. Per-question mode: questions.filter(Choice).length counted all compatible
  //      questions in the template, but analysesByStudent[0].questions only
  //      contains questions for which the backend returned data.  For a student
  //      with data on 1 of 5 compatible questions, groupCount was 5 while the bar
  //      array had length 1, so the single bar was squished at the bottom of a
  //      5-slot domain.
  const groupCount = showStudents
    ? !showQuestions
      ? avgsByStudent[0].questions.length // was: responseUsers.length
      : analysesByStudent[0]?.questions.length ?? 0 // was: questions.filter(Choice).length
    : precomputedTeamAverages?.length ?? safeTeams.length; // was: safeTeams.length

  const id = "analysis-view";
  const { width: chartWidth, ref: chartRef } =
    useResizeDetector<HTMLDivElement>();
  const safeChartWidth = chartWidth ? chartWidth : 800;
  // legendItems must come from the same data source (and same order) as the
  // chart bars so that the color-coded legend entries match the rendered bars.
  //
  // In showStudents && !showQuestions mode the bars come from
  // avgsByStudent[0].questions (one entry per student average).  Using
  // responseUsers here caused two issues:
  //   1. Students present in precomputedStudentAverages but absent from
  //      precomputedAnalyses (e.g. a student who received no individual peer
  //      feedback) were missing from the legend entirely.
  //   2. The sort order of responseUsers may differ from the bar order, so
  //      legend color labels could be assigned to the wrong student.
  const legendItems = showStudents
    ? !showQuestions
      ? (avgsByStudent[0].questions as StudentAverageAnalysis[]).map((q) => {
          const name = q.user.firstName;
          return {
            name: name.length < 12 ? name : name.substring(0, 10) + "...",
          };
        })
      : [...responseUsers]
          .sort((userA, userB) => (userA.firstName > userB.firstName ? 1 : -1))
          .map((user) => ({
            name:
              user.firstName.length < 12
                ? user.firstName
                : user.firstName.substring(0, 10) + "...",
          }))
    : [...safeTeams]
        .sort((teamA, teamB) => (teamA.name > teamB.name ? 1 : -1))
        .map((team) => ({
          name:
            team.name.length < 12
              ? team.name
              : team.name.substring(0, 10) + "...",
        }));
  const legendGutter = 75;
  const legendPadding = 100;
  const itemsPerRow = Math.round(
    chartWidth ? (chartWidth - legendPadding) / (legendGutter * 2) : 10,
  );
  const legendRowGutter = 20;
  const legendHeight =
    Math.ceil(legendItems.length / itemsPerRow) * (14 + legendRowGutter * 2);
  const legendOffset =
    legendItems.length < itemsPerRow
      ? safeChartWidth / 2 - legendItems.length * legendGutter
      : 0;
  const groupOffset = 20;
  const chartHeight = groupCount * (groupOffset + 4) * (1 + 2) + 150;
  const safeChartHeight = chartHeight && chartHeight >= 200 ? chartHeight : 200;
  // Victory auto-computes barWidth from the minimum spacing between x data
  // points.  With a single question (or any small dataset) Victory falls back
  // to a ~4px default because there is no adjacent point to measure the step
  // from.  Derive an explicit barWidth from the design intent encoded in the
  // chart-height formula: each group gets (groupOffset + 4) = 24px, so a bar
  // that occupies groupOffset of those pixels is naturally proportioned.
  const barWidth = groupOffset;
  const barLabel = subjectType === ResponseSubjectType.Peer ? "PEERS" : "VALUE";

  // Chart axis labels: allow up to ~120 chars (truncated at word boundary).
  // Lines wrap at 20 chars; labels longer than 6 lines (~120 chars) get cut off.
  function friendlyLabel(text: string) {
    const truncated = truncateAtWordBoundary(text, 120);
    const lines = divideIntoLines(truncated, 20);
    if (lines.length > 6) lines[5] = lines[5] + "...";
    return lines.slice(0, 6);
  }

  function prependWarning(text: string, outlier: boolean | undefined) {
    return outlier ? `⚠️ ${text}` : text;
  }

  // Custom axis label component: wraps a VictoryLabel with an SVG <title>
  // element so hovering the truncated label reveals the full text natively.
  // (FL-209)
  const TooltipAxisLabel = (props: any) => {
    const { datum } = props;
    const idx = (typeof datum === "number" ? datum : 0) - 1;
    const q = analysesByStudent[0]?.questions[idx];
    const fullText = q ? prependWarning(q.label, q.outlier) : "";
    const displayText = q
      ? prependWarning(friendlyLabel(q.label) as unknown as string, q.outlier)
      : "";
    return (
      <g>
        <title>{fullText}</title>
        <VictoryLabel {...props} text={displayText} />
      </g>
    );
  };

  function divideIntoLines(text: string, targetLength: number) {
    const words = text.split(" ");
    const lines = [];
    let line = "";

    for (let i = 0; i < words.length; i++) {
      if (words[i].length > targetLength) {
        // split the word into chunks that fit the target length
        const chunks = chunkString(words[i], targetLength);
        for (let j = 0; j < chunks.length; j++) {
          lines.push(chunks[j]);
        }
      } else if (line.length + words[i].length + 1 <= targetLength) {
        line += words[i] + " ";
      } else {
        lines.push(line.trim());
        line = words[i] + " "; // Start new line with current word
      }
    }

    // Only push the final line if it's not empty
    const finalLine = line.trim();
    if (finalLine) {
      lines.push(finalLine);
    }

    return lines;
  }

  function chunkString(str: string, length: number) {
    const chunks = [];
    for (let i = 0; i < str.length; i += length) {
      chunks.push(str.substring(i, i + length) + "-");
    }
    chunks[chunks.length - 1] = chunks[chunks.length - 1].slice(0, -1);
    return chunks;
  }

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          mb: 3,
        }}
      >
        {campaign?.id && (
          <ExportMenu
            campaignId={campaign.id}
            campaignReleased={campaign.released !== null}
            selectedTeamId={selectedTeamId}
            selectedUserId={selectedUserId}
            subjectType={subjectType}
            reverse={reverse}
            authorType={authorType}
          />
        )}
      </Box>

      <Stack
        style={{
          height: safeChartHeight,
          ...(reverse ? { display: "none" } : {}),
        }}
        direction="row"
        spacing={3}
        mb={3}
        ref={chartRef}
      >
        {analysesByStudent.length > 0 && (
          <VictoryChart
            horizontal
            theme={VictoryTheme.material}
            padding={{ bottom: 50, left: 100, right: 0 }}
            height={safeChartHeight}
            width={safeChartWidth}
            domain={{
              x: [0.5, groupCount + 0.5],
              y: getDomainBounds(template),
            }}
          >
            <VictoryAxis
              tickValues={analysesByStudent[0].questions.map((_, i) => i + 1)}
              style={{
                axis: { stroke: "white" },
                axisLabel: { padding: 20 },
                grid: { stroke: "white" },
                ticks: { stroke: "white", size: 5 },
                tickLabels: { padding: 5, stroke: "white", fill: "white" },
              }}
              tickLabelComponent={
                <TooltipAxisLabel angle={-45} textAnchor={"end"} />
              }
            />

            <VictoryAxis
              dependentAxis
              label="Rating"
              style={{
                axis: { stroke: "white" },
                axisLabel: { padding: 30 },
                grid: { stroke: "white" },
                ticks: { stroke: "white", size: 5 },
                tickLabels: { padding: 5, stroke: "white", fill: "white" },
              }}
            />

            <VictoryBar
              barWidth={barWidth}
              colorScale={FeedbackLoopColors}
              events={[
                {
                  target: "data",
                  eventHandlers: {
                    onClick: () => {
                      return [
                        {
                          mutation: ({ index }) => {
                            if (!showQuestions) {
                              showStudents
                                ? onUserClick &&
                                  onUserClick(
                                    (
                                      analysesByStudent[0].questions[
                                        index
                                      ] as GenericAverageAnalysis
                                    ).id,
                                  )
                                : onTeamClick &&
                                  onTeamClick(
                                    (
                                      analysesByStudent[0].questions[
                                        index
                                      ] as GenericAverageAnalysis
                                    ).id,
                                  );
                            }

                            return {};
                          },
                        },
                      ];
                    },
                  },
                },
              ]}
              key={
                analysesByStudent[0].user
                  ? analysesByStudent[0].user.id
                  : "static"
              }
              labelComponent={
                <VictoryTooltip constrainToVisibleArea orientation={"top"} />
              }
              name="bar"
              data={analysesByStudent[0].questions.map((question, index) => ({
                x: index + 1,
                y: question.others,
                label: `${
                  question.label
                } (${barLabel}: ${question.others?.toFixed(1)})`,
              }))}
              style={{
                data: {
                  stroke: "white",
                  strokeDasharray: showQuestions
                    ? 0
                    : ({ index }) =>
                        analysesByStudent[0].questions[index as number].outlier
                          ? 3
                          : 0,
                  strokeWidth: 2,
                  fill: showQuestions
                    ? FeedbackLoopColors[0]
                    : ({ index }) => FeedbackLoopColors[(index as number) % 5],
                },
              }}
            />

            <VictoryScatter
              key={
                analysesByStudent[0].user
                  ? analysesByStudent[0].user.id + "others"
                  : "staticothers"
              }
              style={{
                data: {
                  fill: showQuestions
                    ? "white"
                    : (args: CallbackArgs) => {
                        const index =
                          typeof args.index === "number" ? args.index : 0;
                        return analysesByStudent[0].questions[index]?.outlier
                          ? "yellow"
                          : "white";
                      },
                },
              }}
              size={6}
              labelComponent={
                <VictoryTooltip constrainToVisibleArea orientation={"top"} />
              }
              name="scatter"
              data={analysesByStudent[0].questions.map((question, index) => ({
                x: index + 1,
                y: question.self,
                label: `${question.label} (SELF: ${question.self?.toFixed(1)})`,
                others: question.others,
              }))}
            />
          </VictoryChart>
        )}
      </Stack>

      {!showQuestions && (
        <div>
          <VictoryLegend
            x={legendPadding + legendOffset}
            // y={50}
            title=""
            height={legendHeight}
            width={chartWidth}
            centerTitle
            orientation="horizontal"
            gutter={legendGutter}
            rowGutter={legendRowGutter}
            style={{
              border: { stroke: "none", width: "100%" },
              title: { fontSize: 20, fill: "white" },
              labels: { fontSize: 14, fill: "white" },
            }}
            colorScale={FeedbackLoopColors}
            itemsPerRow={itemsPerRow}
            data={legendItems}
          />
        </div>
      )}

      <Stack id={id} direction="column" spacing={3}>
        {outliers.length > 0 && !reverse && (
          <DataGrid
            autoHeight={true}
            initialState={{
              sorting: {
                sortModel: [{ field: "gap", sort: "desc" }],
              },
            }}
            hideFooter={!outliers || outliers.length < 100}
            rows={outliers as GenericAverageAnalysis[]}
            components={{
              Toolbar: GridHeaderFactory(
                "Outliers",
                "#522",
                <Warning color="warning" fontSize="inherit" />,
              ),
            }}
            sx={{
              backgroundColor: "black",
              mb: 1,
              cursor: "pointer",
            }}
            columns={
              showStudents
                ? subjectType === ResponseSubjectType.Peer
                  ? IndividualSelfPeerOutlierColumns
                  : IndividualOutlierColumns
                : subjectType === ResponseSubjectType.Peer
                  ? TeamSelfPeerOutlierColumns
                  : TeamOutlierColumns
            }
            pagination={undefined}
            onRowClick={(params) =>
              showStudents
                ? onUserClick && onUserClick(params.row.id)
                : onTeamClick && onTeamClick(params.row.id)
            }
          />
        )}

        <DataGrid
          autoHeight={true}
          initialState={{
            sorting: {
              sortModel: [{ field: "peers", sort: "desc" }],
            },
          }}
          hideFooter={!averageAnalyses || averageAnalyses.length < 100}
          rows={averageAnalyses}
          components={{
            Toolbar: GridHeaderFactory("Averages"),
          }}
          sx={{ backgroundColor: "black", mb: 1 }}
          columns={
            subjectType === ResponseSubjectType.Peer
              ? reverse
                ? AverageGivenColumns
                : getAverageSelfPeerColumns(multipleRaterTypes)
              : AverageColumns
          }
          pagination={undefined}
          getRowHeight={() => "auto"}
        />

        {analysesToObjectArray(
          analyses,
          aggregates,
          pointRows,
          sections,
          user,
        ).map((section) => (
          <>
            {section.analyses.length > 0 && (
              <DataGrid
                autoHeight={true}
                key={section.id}
                initialState={{
                  sorting: {
                    sortModel: [{ field: "gap", sort: "desc" }],
                  },
                }}
                hideFooter={!section.analyses || section.analyses.length < 100}
                rows={section.analyses}
                components={{
                  Toolbar: GridHeaderFactory(section.section),
                }}
                sx={{ backgroundColor: "black", mb: 1 }}
                columns={
                  showQuestions
                    ? subjectType === ResponseSubjectType.Peer
                      ? reverse
                        ? IndividualGivenColumns
                        : getIndividualSelfPeerColumns(multipleRaterTypes)
                      : IndividualColumns
                    : subjectType === ResponseSubjectType.Peer
                      ? reverse
                        ? GroupGivenColumns
                        : getGroupSelfPeerColumns(multipleRaterTypes)
                      : GroupColumns
                }
                pagination={undefined}
                getRowHeight={() => "auto"}
              />
            )}

            {section.yesNoAnalyses.length > 0 && (
              <DataGrid
                autoHeight={true}
                key={section.id + "-yesno"}
                initialState={{
                  sorting: {
                    sortModel: [{ field: "gap", sort: "desc" }],
                  },
                }}
                hideFooter={
                  !section.yesNoAnalyses || section.yesNoAnalyses.length < 100
                }
                rows={section.yesNoAnalyses}
                components={{
                  Toolbar: GridHeaderFactory(
                    `${section.section} - Yes/No Questions`,
                  ),
                }}
                sx={{ backgroundColor: "black", mb: 1 }}
                columns={
                  showQuestions
                    ? subjectType === ResponseSubjectType.Peer
                      ? getIndividualSelfPeerYesNoColumns(multipleRaterTypes)
                      : IndividualYesNoColumns
                    : subjectType === ResponseSubjectType.Peer
                      ? getGroupSelfPeerYesNoColumns(multipleRaterTypes)
                      : GroupYesNoColumns
                }
                pagination={undefined}
                getRowHeight={() => "auto"}
              />
            )}

            {section.aggregates.map((aggregate) => (
              <DataGrid
                autoHeight={true}
                key={aggregate.id}
                hideFooter={
                  reverse
                    ? !aggregate.givenComments ||
                      aggregate.givenComments.length < 100
                    : !aggregate.givenComments ||
                      aggregate.receivedComments.length < 100
                }
                rows={
                  reverse ? aggregate.givenComments : aggregate.receivedComments
                }
                components={{
                  Toolbar: GridHeaderFactory(
                    `${section.section} - ${aggregate.question}`,
                  ),
                }}
                sx={{ backgroundColor: "black", mb: 1 }}
                columns={
                  showQuestions
                    ? reverse
                      ? IndividualGivenTextColumns
                      : IndividualTextColumns
                    : GroupTextColumns
                }
                getRowHeight={() => "auto"}
              />
            ))}

            {section.pointRows.length > 0 && (
              <DataGrid
                autoHeight={true}
                key={section.id}
                hideFooter={
                  !section.pointRows || section.pointRows.length < 100
                }
                rows={section.pointRows}
                components={{
                  Toolbar: GridHeaderFactory(
                    `${section.section} (Points allocation)`,
                  ),
                }}
                sx={{ backgroundColor: "black", mb: 1 }}
                columns={
                  showQuestions ? IndividualPointColumns : GroupPointColumns
                }
                pagination={undefined}
                getRowHeight={() => "auto"}
              />
            )}
          </>
        ))}
      </Stack>

      {user && (campaign?.loopInstructor || campaign?.loopStudent) && (
        <InstructorLoopResponse
          campaignId={campaign.id}
          userId={user.id}
          showStudent={campaign.loopStudent}
          showInstructor={campaign.loopInstructor}
          hasRatings={
            analyses.some((a) => a.others !== undefined) ||
            aggregates.some((agg) =>
              agg.comments.some(
                (c) =>
                  c.recipient.id === user.id && c.respondent.id !== user.id,
              ),
            )
          }
        />
      )}
    </>
  );
};
